import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import restaurantService, { PlanFeaturesPayload, Restaurant } from '../../services/restaurantService';
import gamesService from '../../services/gamesService';
import { hasCustomerFeature } from '../../utils/planFeatures';
import { gamesSignInPath } from '../../utils/gamesAuth';
import { useAppSelector } from '../../store/hooks';
import { useEnabledGames } from './useEnabledGames';
import { ALL_GAMES, GameMeta, GameTodayResult } from '../../types/games';
import CopyRedemptionCode from './CopyRedemptionCode';
import './GamesHub.css';

function resultSummary(play: GameTodayResult): string {
  if (play.gameType === 'BILL_SPLIT') {
    return `${play.resultSegment} pays`;
  }
  return play.resultSegment;
}

const GamesHub: React.FC = () => {
  const { appName } = useParams<{ appName: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, authReady } = useAppSelector((state) => state.user);
  const [planFeatures, setPlanFeatures] = useState<PlanFeaturesPayload | undefined>(undefined);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [todayPlays, setTodayPlays] = useState<GameTodayResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [todayDrawerOpen, setTodayDrawerOpen] = useState(false);
  const { visibleGames, loading: enabledGamesLoading, hasEnabledGames } = useEnabledGames(appName);

  useEffect(() => {
    if (!authReady || !appName || isAuthenticated) return;
    navigate(gamesSignInPath(appName), { replace: true });
  }, [authReady, isAuthenticated, appName, navigate]);

  useEffect(() => {
    if (!appName || !isAuthenticated) return;
    Promise.all([
      restaurantService.getRestaurant(appName).then((res) => {
        setPlanFeatures(res.features);
        setRestaurant(res.restaurant ?? null);
      }),
      gamesService.getTodayResults(appName).then((res) => setTodayPlays(res.plays || [])).catch(() => setTodayPlays([])),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [appName, isAuthenticated]);

  const gamesEnabled = hasCustomerFeature(planFeatures, 'in_app_games');

  const playsByGame = useMemo(() => {
    const map = new Map<string, GameTodayResult>();
    todayPlays.forEach((play) => {
      if (play.gameType && !map.has(play.gameType)) {
        map.set(play.gameType, play);
      }
    });
    return map;
  }, [todayPlays]);

  const winsToday = useMemo(
    () => todayPlays.filter((play) => play.isWin || play.redemptionCode),
    [todayPlays]
  );

  useEffect(() => {
    if (!todayDrawerOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setTodayDrawerOpen(false);
    };
    document.body.classList.add('games-today-drawer-open');
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.classList.remove('games-today-drawer-open');
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [todayDrawerOpen]);

  function handleGameClick(game: GameMeta) {
    navigate(`/restaurants/${appName}/games/${game.gameType}`);
  }

  const todayResultsList = (
    <div className="games-hub-today__list">
      {todayPlays.map((play, index) => {
        const game = ALL_GAMES.find((g) => g.gameType === play.gameType);
        return (
          <div key={`${play.gameType}-${index}`} className="games-hub-today__item">
            <div className="games-hub-today__item-head">
              <span>{game?.emoji ?? '🎮'}</span>
              <strong>{game?.label ?? play.gameType}</strong>
              {(play.isWin || play.redemptionCode) && (
                <span className="games-hub-today__win-badge">Win</span>
              )}
            </div>
            <div className="games-hub-today__item-result">{resultSummary(play)}</div>
            {play.redemptionCode && (
              <CopyRedemptionCode
                code={play.redemptionCode}
                className="copy-redemption-code copy-redemption-code--compact games-hub-today__code-row"
                codeClassName="copy-redemption-code__value"
                buttonClassName="copy-redemption-code__btn"
              />
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      <Header />
      <div className="games-hub">
        <div className="games-hub-header">
          <div className="games-hub-header__title">🎮 Play &amp; Win</div>
          <div className="games-hub-header__sub">Spin, scratch, roll — win discounts on your meal</div>
        </div>

        {loading || enabledGamesLoading || !authReady || !isAuthenticated ? (
          <div className="games-hub-empty">Loading games…</div>
        ) : !gamesEnabled ? (
          <div className="games-hub-empty">
            Games are not available at this restaurant right now.
          </div>
        ) : !hasEnabledGames ? (
          <div className="games-hub-empty">
            No games are available at this restaurant right now.
          </div>
        ) : (
          <>
            {todayPlays.length > 0 && (
              <button
                type="button"
                className="games-hub-today-trigger"
                onClick={() => setTodayDrawerOpen(true)}
              >
                <span className="games-hub-today-trigger__label">Today&apos;s results</span>
                <span className="games-hub-today-trigger__meta">
                  {todayPlays.length} {todayPlays.length === 1 ? 'play' : 'plays'}
                  {winsToday.length > 0 && ` · ${winsToday.length} win${winsToday.length > 1 ? 's' : ''}`}
                </span>
                <span className="games-hub-today-trigger__chevron" aria-hidden>
                  ›
                </span>
              </button>
            )}

            <div className="games-hub-grid">
              {visibleGames.map((game) => {
                const played = playsByGame.get(game.gameType);
                return (
                  <button
                    key={game.gameType}
                    className="games-hub-card"
                    onClick={() => handleGameClick(game)}
                  >
                    <div className="games-hub-card__emoji">{game.emoji}</div>
                    <div className="games-hub-card__label">{game.label}</div>
                    <div className="games-hub-card__desc">{game.description}</div>
                    {played && (
                      <div className={`games-hub-card__result${played.isWin || played.redemptionCode ? ' is-win' : ''}`}>
                        Today: {resultSummary(played)}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {todayDrawerOpen && todayPlays.length > 0 && (
              <div
                className="games-today-drawer-overlay"
                onClick={() => setTodayDrawerOpen(false)}
                role="presentation"
              >
                <div
                  className="games-today-drawer"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="games-today-drawer-title"
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="games-today-drawer__handle" aria-hidden />
                  <div className="games-today-drawer__header">
                    <h2 id="games-today-drawer-title">Today&apos;s results</h2>
                    <button
                      type="button"
                      className="games-today-drawer__close"
                      onClick={() => setTodayDrawerOpen(false)}
                      aria-label="Close today's results"
                    >
                      ×
                    </button>
                  </div>
                  <div className="games-today-drawer__body">
                    {todayResultsList}
                    {winsToday.length > 0 && (
                      <p className="games-hub-today__hint">
                        Show your redemption code to staff when you request the bill.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <Footer variant="restaurant" restaurant={restaurant} />
    </div>
  );
};

export default GamesHub;
